import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cx } from "../../utils";

const ToastContext = createContext(() => {});

const tones = {
  success: { icon: CheckCircle2, ring: "ring-emerald-200/70", color: "text-emerald-600" },
  error: { icon: AlertCircle, ring: "ring-rose-200/70", color: "text-rose-600" },
  info: { icon: Info, ring: "ring-brand-200/70", color: "text-brand-600" },
};

/**
 * Wraps the app once (see src/index.js). `useToast()` returns a
 * `toast(message, tone)` function usable from any screen.
 */
export const ToastProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message, tone = "success") => {
      const id = Date.now() + Math.random();
      setItems((prev) => [...prev, { id, message, tone }]);
      setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 pointer-events-none">
        {items.map(({ id, message, tone }) => {
          const t = tones[tone] || tones.info;
          const Icon = t.icon;
          return (
            <div
              key={id}
              role="status"
              className={cx(
                "pointer-events-auto flex items-start gap-2.5 rounded-xl bg-white px-4 py-3",
                "shadow-card-elevated ring-1 animate-fade-up max-w-sm",
                t.ring,
              )}
            >
              <Icon size={16} className={cx("mt-0.5 shrink-0", t.color)} />
              <p className="text-sm text-ink-700 leading-snug">{message}</p>
              <button
                onClick={() => dismiss(id)}
                className="ml-1 shrink-0 text-ink-400 hover:text-ink-700"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

export default ToastProvider;
